import { useId, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { isValidHex, type CmsColor } from "@/lib/cms/cms-theme.constants";
import {
  THEME_COLOR_CSS,
  type ColorRef,
  type ThemeColorKey,
} from "@/lib/cms-puck/block-style";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, ChevronDown } from "lucide-react";
import { CmsColorPickerField } from "./CmsColorPickerField";
import {
  CMS_PALETTE_FIELD_LABEL_CLASS,
  CMS_PALETTE_HEX_INPUT_CLASS,
} from "./cms-color-input-styles";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";

/**
 * ColorField — picker de couleur NON-POLLUANT pour la sidebar « Style » des blocs.
 *
 * STRICTEMENT distinct de `PaletteColorPicker` : il N'IMPORTE PAS
 * `useEnsurePaletteColor` et n'appelle AUCUNE API thème. Une couleur libre reste
 * une valeur figée locale au bloc ({source:'custom',hex}), JAMAIS ajoutée à la
 * palette (0 écriture API). Une couleur palette stocke une RÉFÉRENCE
 * ({source:'palette',id}) qui suit les changements de hex de la palette.
 *
 * 4 modes : « Hérite » (null) | couleur du thème de la page ({source:'theme',key},
 * suit le fond / texte de la page) | référence palette | couleur libre (hex).
 *
 * UI : un Popover (déclencheur compact = pastille + libellé de la sélection
 * courante) pour rester lisible avec 30+ couleurs. Le panneau contient l'option
 * « Hérite », une grille de pastilles SCROLLABLE filtrable par recherche
 * (nom/hex), et une section couleur libre. Contrat de valeur/props INCHANGÉ.
 */
type Props = {
  label: string;
  value: ColorRef;
  onChange: (value: ColorRef) => void;
  colors: CmsColor[];
};

const SWATCH_BTN_CLASS =
  "group relative flex flex-col items-center gap-1 rounded-md p-1 text-center transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-400";

const SWATCH_CHIP_CLASS =
  "h-8 w-full rounded-md border border-slate-200 ring-1 ring-inset ring-black/10";

/**
 * Libellé par défaut (FR) de chaque couleur du thème ; l'ordre des clés est
 * l'ordre d'affichage (le `Record` complet garantit qu'aucune clé ne manque).
 */
const THEME_COLOR_LABELS: Record<ThemeColorKey, string> = {
  text: "Texte",
  muted: "Texte atténué",
  link: "Lien (survol)",
  background: "Fond",
  surface: "Surface",
  border: "Bordure",
  borderStrong: "Bordure marquée",
};

const THEME_COLOR_KEYS = Object.keys(THEME_COLOR_LABELS) as ThemeColorKey[];

/**
 * Peint une couleur du thème PAR-DESSUS le fond de la page : les couleurs
 * semi-transparentes (atténué, bordures) s'affichent comme sur la page.
 */
function themeSwatchStyle(key: ThemeColorKey): React.CSSProperties {
  const color = THEME_COLOR_CSS[key];
  return {
    backgroundColor: "var(--cms-page-bg, #ffffff)",
    backgroundImage: `linear-gradient(${color}, ${color})`,
  };
}

/**
 * Pastille d'aperçu (carrée) utilisée dans le déclencheur et l'option Hérite.
 * `color` = toute couleur CSS (hex, ou expression d'une couleur du thème).
 */
function ColorChip({
  color,
  themeKey,
  inherit,
  className,
}: {
  color?: string;
  /** Couleur du thème : peinte sur le fond de la page (cf. themeSwatchStyle). */
  themeKey?: ThemeColorKey | null;
  inherit?: boolean;
  className?: string;
}) {
  if (inherit || (!color && !themeKey)) {
    // Damier « transparent / hérite ».
    return (
      <span
        className={cn(
          "inline-block h-5 w-5 shrink-0 rounded border border-slate-200 bg-white",
          className,
        )}
        style={{
          backgroundImage:
            "linear-gradient(45deg, #cbd5e1 25%, transparent 25%), linear-gradient(-45deg, #cbd5e1 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #cbd5e1 75%), linear-gradient(-45deg, transparent 75%, #cbd5e1 75%)",
          backgroundSize: "8px 8px",
          backgroundPosition: "0 0, 0 4px, 4px -4px, -4px 0",
        }}
      />
    );
  }
  return (
    <span
      className={cn(
        "inline-block h-5 w-5 shrink-0 rounded border border-slate-200 ring-1 ring-inset ring-black/10",
        className,
      )}
      style={themeKey ? themeSwatchStyle(themeKey) : { backgroundColor: color }}
    />
  );
}

export function ColorField({ label, value, onChange, colors }: Props) {
  const vt = useVisibleT();
  const themeGroupId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const isInherit = value == null;
  const selectedPaletteId =
    value && value.source === "palette" ? value.id : null;
  const isCustom = value != null && value.source === "custom";
  const selectedThemeKey = value && value.source === "theme" ? value.key : null;
  const customHex = isCustom ? value.hex : "";
  const customPreviewHex = isValidHex(customHex) ? customHex : "#000000";

  // Résout l'état courant en (pastille + libellé) pour le déclencheur.
  const selectedPaletteColor =
    selectedPaletteId != null
      ? colors.find((c) => c.id === selectedPaletteId)
      : undefined;

  const themeLabel = (key: ThemeColorKey) =>
    vt(`bo.cms.colorField.theme.${key}`, THEME_COLOR_LABELS[key]);

  const triggerColor = isCustom
    ? isValidHex(customHex)
      ? customHex
      : undefined
    : selectedPaletteColor?.hex;
  const triggerLabel = isInherit
    ? vt("bo.cms.colorField.inherit", "Hérite")
    : selectedThemeKey
      ? themeLabel(selectedThemeKey)
      : isCustom
        ? customHex || vt("bo.cms.colorField.customColor", "Couleur libre")
        : (selectedPaletteColor?.name ??
          vt("bo.cms.colorField.deletedColor", "Couleur supprimée"));

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return colors;
    return colors.filter(
      (c) =>
        c.name.toLowerCase().includes(q) || c.hex.toLowerCase().includes(q),
    );
  }, [colors, query]);

  return (
    <div className="space-y-2">
      <label className={CMS_PALETTE_FIELD_LABEL_CLASS}>{label}</label>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`${label} — ${vt("bo.cms.colorField.chooseColorAria", "choisir une couleur")}`}
            className="flex w-full items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-left text-sm shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-400"
          >
            <ColorChip
              color={triggerColor}
              themeKey={selectedThemeKey}
              inherit={isInherit}
            />
            <span className="min-w-0 flex-1 truncate text-slate-700">
              {triggerLabel}
            </span>
            <ChevronDown size={14} className="shrink-0 text-slate-400" />
          </button>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          sideOffset={6}
          className="w-72 p-3"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="space-y-3">
            {/* Option « Hérite » (null) */}
            <button
              type="button"
              onClick={() => onChange(null)}
              className={cn(
                "flex w-full items-center gap-2 rounded-md border px-2.5 py-1.5 text-left text-sm transition-colors",
                isInherit
                  ? "border-slate-400 bg-slate-100 text-slate-800"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
              )}
            >
              <ColorChip inherit />
              <span className="flex-1">
                {vt("bo.cms.colorField.inheritOption", "Hérite (aucune couleur)")}
              </span>
              {isInherit && <Check size={14} className="text-slate-600" />}
            </button>

            {/* Couleurs du thème : relatives au fond / texte de la page */}
            <div className="space-y-1.5">
              <span
                id={themeGroupId}
                className="block text-xs font-medium text-slate-600"
              >
                {vt("bo.cms.colorField.theme.title", "Couleurs de la page")}
              </span>
              <div
                role="group"
                aria-labelledby={themeGroupId}
                className="grid grid-cols-3 gap-1.5"
              >
                {THEME_COLOR_KEYS.map((key) => {
                  const isSelected = selectedThemeKey === key;
                  const name = themeLabel(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      title={name}
                      aria-label={name}
                      aria-pressed={isSelected}
                      onClick={() => onChange({ source: "theme", key })}
                      className={cn(
                        SWATCH_BTN_CLASS,
                        isSelected && "bg-slate-100",
                      )}
                    >
                      <span
                        className={cn(
                          SWATCH_CHIP_CLASS,
                          isSelected && "ring-2 ring-slate-500",
                        )}
                        style={themeSwatchStyle(key)}
                      />
                      <span className="line-clamp-1 w-full text-[10px] leading-tight text-slate-600">
                        {name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Palette : recherche + grille scrollable */}
            <div className="space-y-2">
              <input
                type="text"
                value={query}
                placeholder={vt(
                  "bo.cms.colorField.searchPlaceholder",
                  "Rechercher une couleur…",
                )}
                aria-label={vt(
                  "bo.cms.colorField.searchAria",
                  "Rechercher une couleur dans la palette",
                )}
                className="h-8 w-full rounded-md border border-slate-200 bg-white px-2.5 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                onChange={(e) => setQuery(e.target.value)}
              />
              {filtered.length === 0 ? (
                <p className="px-1 py-3 text-center text-xs text-slate-400">
                  {colors.length === 0
                    ? vt(
                        "bo.cms.colorField.emptyPalette",
                        "Aucune couleur dans la palette.",
                      )
                    : vt("bo.cms.colorField.noResults", "Aucun résultat.")}
                </p>
              ) : (
                <div className="max-h-[240px] overflow-auto pr-1">
                  <div className="grid grid-cols-3 gap-1.5">
                    {filtered.map((color) => {
                      const isSelected = selectedPaletteId === color.id;
                      return (
                        <button
                          key={color.id}
                          type="button"
                          title={`${color.name} (${color.hex})`}
                          aria-label={`${color.name} (${color.hex})`}
                          onClick={() =>
                            onChange({ source: "palette", id: color.id })
                          }
                          className={cn(
                            SWATCH_BTN_CLASS,
                            isSelected && "bg-slate-100",
                          )}
                        >
                          <span
                            className={cn(
                              SWATCH_CHIP_CLASS,
                              isSelected && "ring-2 ring-slate-500",
                            )}
                            style={{ backgroundColor: color.hex }}
                          />
                          <span className="line-clamp-1 w-full text-[10px] leading-tight text-slate-600">
                            {color.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Couleur LIBRE → {source:'custom', hex} (figée, jamais ajoutée à la palette) */}
            <div className="space-y-1.5 border-t border-slate-100 pt-3">
              <span className="block text-xs font-medium text-slate-600">
                {vt("bo.cms.colorField.customColor", "Couleur libre")}
              </span>
              <div className="flex items-center gap-2">
                <CmsColorPickerField
                  value={customPreviewHex}
                  aria-label={`${label} — ${vt("bo.cms.colorField.customColorAria", "couleur libre")}`}
                  onCommit={(hex) => onChange({ source: "custom", hex })}
                  triggerClassName={cn(isCustom && "ring-2 ring-slate-500")}
                />
                <input
                  type="text"
                  value={customHex}
                  placeholder={vt(
                    "bo.cms.colorField.customHexPlaceholder",
                    "#RRGGBB (libre)",
                  )}
                  className={CMS_PALETTE_HEX_INPUT_CLASS}
                  onChange={(e) => {
                    const next = e.target.value.trim();
                    if (next === "") {
                      onChange(null);
                      return;
                    }
                    // On stocke tel quel ; le rendu ignore un hex invalide (hérite).
                    onChange({ source: "custom", hex: next });
                  }}
                />
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
